import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import { CourseStopEntity } from 'src/entities/Course.entity';

import { stayMinutesLabel } from './courseMasterUtils';

export const STOP_COLS = '28px 28px 92px minmax(160px, 1fr) 118px 118px 64px 36px';
const muted = 'text.secondary';
type TimeField = 'arrivalTime' | 'departureTime';
type Props = {
  stop: CourseStopEntity;
  /** 適用終了・対象外を除いた地点の中の位置（1 から）。対象外の行は null で「—」 */
  position: number | null;
  invalid: boolean;
  unsaved: boolean;
  disabled: boolean;
  onTime: (field: TimeField, value: string | null) => void;
  onEnd: () => void;
};

/**
 * 地点 1 行。時刻は blur で確定（変わったときだけ onTime）。invalid は時刻順の検査で引っかかった行。
 * unsaved（負の仮 id の追加行）は適用終了の API を呼べない（未保存）ので「取り消す」を出す
 */
export default function StopRow({ stop, position, invalid, unsaved, disabled, onTime, onEnd }: Props) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: stop.id, disabled });
  const name = stop.place.name;
  const time = (field: TimeField, label: string) => (
    <TextField
      key={`${stop.id}-${field}-${stop[field] ?? ''}`}
      type="time"
      size="small"
      defaultValue={stop[field] ?? ''}
      disabled={disabled}
      slotProps={{
        htmlInput: {
          'aria-label': `${name}の${label}`,
          'aria-invalid': invalid,
          'aria-describedby': invalid ? 'route-time-error' : undefined,
        },
      }}
      onBlur={(e) => {
        const next = e.target.value || null;
        if (next === stop[field]) return;
        onTime(field, next);
        // 受け付けた変更は stop が変わり key で作り直されるので、ここで戻すのは捨てられた変更（保存中の重なり）だけになる。
        // Why not 制御コンポーネント: 入力途中の値ごとに draft を変えると検査と保存が blur より前に走る
        e.target.value = stop[field] ?? '';
      }}
    />
  );
  return (
    <Box
      ref={setNodeRef}
      data-testid="course-stop"
      aria-invalid={invalid}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'grid',
        gridTemplateColumns: STOP_COLS,
        minWidth: 900,
        gap: 1,
        alignItems: 'center',
        px: 1,
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: invalid ? 'rgba(244, 67, 54, 0.08)' : 'background.paper',
        outline: invalid ? '2px solid' : 'none',
        outlineColor: 'error.main',
        opacity: stop.excluded ? 0.55 : 1,
        position: 'relative',
        zIndex: isDragging ? 5 : 'auto',
      }}
    >
      <IconButton
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        size="small"
        aria-label={`${name}をドラッグで並び替え`}
        disabled={disabled}
        sx={{ p: 0, cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none', color: muted }}
      >
        <DragIndicatorRoundedIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Box sx={{ fontWeight: 700, fontSize: 15 }}>{position ?? '—'}</Box>
      <Box sx={{ fontSize: 13, color: muted }}>{stop.place.internalLocationCode ?? '—'}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: 14, fontWeight: 600, textDecoration: stop.excluded ? 'line-through' : 'none' }}>{name}</Box>
        <Box sx={{ fontSize: 12, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stop.place.address}</Box>
      </Box>
      {time('arrivalTime', '到着')}
      {time('departureTime', '出発')}
      <Box sx={{ fontSize: 13, color: muted }}>{stayMinutesLabel(stop.arrivalTime, stop.departureTime)}</Box>
      <Tooltip title={unsaved ? '追加を取り消す' : 'コースから削除（適用終了）'}>
        <span>
          <IconButton
            size="small"
            onClick={onEnd}
            disabled={disabled}
            aria-label={unsaved ? `${name}の追加を取り消す` : `${name}をコースから削除`}
          >
            {unsaved ? <CloseRoundedIcon sx={{ fontSize: 18 }} /> : <EventBusyOutlinedIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
